import React, { useState, useEffect, useCallback } from 'react'
import type { Role, Permission, PermissionsByCategory } from '../../types/roles'
import { groupPermissionsByCategory } from '../../types/roles'
import { rolesApi } from '../../services/roles'
import { Modal } from '../shared/Modal'

interface RoleFormData {
  name: string
  permission_ids: string[]
}

// Category display names
const CATEGORY_LABELS: Record<string, string> = {
  inventory: 'Inventory',
  content: 'Content',
  reports: 'Reports',
  settings: 'Settings',
  users: 'Users',
}

export function RoleManagement(): React.ReactElement {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{
    role: Role
    reassignTo: string
  } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await rolesApi.getRoles()
      setRoles(data.roles)
      setPermissions(data.permissions)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  const handleCreate = async (data: RoleFormData) => {
    try {
      setSaving(true)
      setFormError(null)
      await rolesApi.createRole(data)
      await loadRoles()
      setIsCreating(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create role')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (data: RoleFormData) => {
    if (!editingRole) return
    try {
      setSaving(true)
      setFormError(null)
      await rolesApi.updateRole(editingRole.id, data)
      await loadRoles()
      setEditingRole(null)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to update role')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      setSaving(true)
      setFormError(null)
      await rolesApi.deleteRole(deleteConfirm.role.id, {
        reassign_to: deleteConfirm.reassignTo || undefined,
      })
      await loadRoles()
      setDeleteConfirm(null)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete role')
    } finally {
      setSaving(false)
    }
  }

  const permissionsByCategory = groupPermissionsByCategory(permissions)
  const customRoles = roles.filter((r) => !r.built_in)
  const builtInRoles = roles.filter((r) => r.built_in)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p className="font-medium">Error loading roles</p>
        <p className="text-sm">{error}</p>
        <button
          onClick={loadRoles}
          className="mt-2 text-sm text-red-800 underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Role Management</h1>
          <p className="text-grey text-sm mt-1">
            Manage custom roles and permissions for your account
          </p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded font-medium flex items-center gap-2"
        >
          <i className="fa fa-plus" />
          Create Role
        </button>
      </div>

      {/* Built-in Roles */}
      <div>
        <h2 className="text-lg font-semibold text-secondary mb-3">Built-in Roles</h2>
        <div className="bg-gray-50 border rounded overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-secondary">Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-secondary">
                  Permissions
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-secondary">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {builtInRoles.map((role) => (
                <tr key={role.id} className="bg-white">
                  <td className="px-4 py-3">
                    <span className="font-medium text-secondary capitalize">{role.name}</span>
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                      Built-in
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-grey">
                    {role.permission_ids.length === permissions.length
                      ? 'All permissions'
                      : `${role.permission_ids.length} permissions`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditingRole(role)}
                      className="text-secondary hover:text-primary text-sm"
                    >
                      <i className="fa fa-eye mr-1" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Custom Roles */}
      <div>
        <h2 className="text-lg font-semibold text-secondary mb-3">Custom Roles</h2>
        {customRoles.length === 0 ? (
          <div className="bg-gray-50 border rounded px-4 py-8 text-center text-grey">
            <i className="fa fa-users text-3xl mb-2 opacity-50" />
            <p>No custom roles yet</p>
            <p className="text-sm">Create a custom role to assign specific permissions to users</p>
          </div>
        ) : (
          <div className="border rounded overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-secondary">Name</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-secondary">
                    Permissions
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y bg-white">
                {customRoles.map((role) => (
                  <tr key={role.id}>
                    <td className="px-4 py-3">
                      <span className="font-medium text-secondary">{role.name}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-grey">
                      {role.permission_ids.length} permissions
                    </td>
                    <td className="px-4 py-3 text-right space-x-3">
                      <button
                        onClick={() => setEditingRole(role)}
                        className="text-secondary hover:text-primary text-sm"
                      >
                        <i className="fa fa-edit mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ role, reassignTo: '' })}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        <i className="fa fa-trash mr-1" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(isCreating || editingRole) && (
        <RoleFormModal
          role={editingRole}
          permissionsByCategory={permissionsByCategory}
          onSave={editingRole ? handleUpdate : handleCreate}
          onCancel={() => {
            setEditingRole(null)
            setIsCreating(false)
            setFormError(null)
          }}
          saving={saving}
          error={formError}
          readOnly={editingRole?.built_in}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteRoleModal
          role={deleteConfirm.role}
          roles={customRoles.filter((r) => r.id !== deleteConfirm.role.id)}
          builtInRoles={builtInRoles}
          reassignTo={deleteConfirm.reassignTo}
          onReassignChange={(id) => setDeleteConfirm({ ...deleteConfirm, reassignTo: id })}
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteConfirm(null)
            setFormError(null)
          }}
          saving={saving}
          error={formError}
        />
      )}
    </div>
  )
}

// Role Form Modal Component
interface RoleFormModalProps {
  role: Role | null
  permissionsByCategory: PermissionsByCategory
  onSave: (data: RoleFormData) => void
  onCancel: () => void
  saving: boolean
  error: string | null
  readOnly?: boolean
}

function RoleFormModal({
  role,
  permissionsByCategory,
  onSave,
  onCancel,
  saving,
  error,
  readOnly,
}: RoleFormModalProps): React.ReactElement {
  const [name, setName] = useState(role?.name || '')
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
    new Set(role?.permission_ids || [])
  )

  const handleTogglePermission = (id: string) => {
    if (readOnly) return
    const newSelected = new Set(selectedPermissions)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedPermissions(newSelected)
  }

  const handleToggleCategory = (category: string) => {
    if (readOnly) return
    const categoryPermissions = permissionsByCategory[category] || []
    const categoryIds = categoryPermissions.map((p) => p.id)
    const allSelected = categoryIds.every((id) => selectedPermissions.has(id))

    const newSelected = new Set(selectedPermissions)
    if (allSelected) {
      categoryIds.forEach((id) => newSelected.delete(id))
    } else {
      categoryIds.forEach((id) => newSelected.add(id))
    }
    setSelectedPermissions(newSelected)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (readOnly) return
    onSave({
      name: name.trim(),
      permission_ids: Array.from(selectedPermissions),
    })
  }

  const isEdit = role !== null
  const title = readOnly
    ? `View Role: ${role?.name}`
    : isEdit
      ? `Edit Role: ${role?.name}`
      : 'Create New Role'

  return (
    <Modal open={true} onClose={onCancel} size="xl" theme="light">
      <Modal.Header onClose={onCancel}>{title}</Modal.Header>
      <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
        <Modal.Body>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            {/* Role Name */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={readOnly}
                placeholder="e.g., Sales Manager"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            {/* Permissions */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-3">Permissions</label>
              <div className="space-y-4">
                {Object.entries(permissionsByCategory).map(([category, categoryPermissions]) => {
                  const categoryIds = categoryPermissions.map((p) => p.id)
                  const selectedCount = categoryIds.filter((id) =>
                    selectedPermissions.has(id)
                  ).length
                  const allSelected = selectedCount === categoryIds.length

                  return (
                    <div key={category} className="border rounded">
                      <div
                        className={`flex items-center justify-between px-4 py-2 bg-gray-50 border-b ${
                          readOnly ? '' : 'cursor-pointer hover:bg-gray-100'
                        }`}
                        onClick={() => handleToggleCategory(category)}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => handleToggleCategory(category)}
                            disabled={readOnly}
                            className="rounded text-primary focus:ring-primary"
                          />
                          <span className="font-medium text-secondary">
                            {CATEGORY_LABELS[category] || category}
                          </span>
                        </div>
                        <span className="text-sm text-grey">
                          {selectedCount}/{categoryIds.length} selected
                        </span>
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-2">
                        {categoryPermissions.map((permission) => (
                          <label
                            key={permission.id}
                            className={`flex items-center gap-2 text-sm ${
                              readOnly ? '' : 'cursor-pointer'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPermissions.has(permission.id)}
                              onChange={() => handleTogglePermission(permission.id)}
                              disabled={readOnly}
                              className="rounded text-primary focus:ring-primary"
                            />
                            <span className="text-grey">{permission.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Modal.Body>

          {/* Footer */}
          <Modal.Footer>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border rounded text-grey hover:bg-gray-100"
              disabled={saving}
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : isEdit ? (
                  'Save Changes'
                ) : (
                  'Create Role'
                )}
              </button>
            )}
          </Modal.Footer>
        </form>
    </Modal>
  )
}

// Delete Role Modal Component
interface DeleteRoleModalProps {
  role: Role
  roles: Role[]
  builtInRoles: Role[]
  reassignTo: string
  onReassignChange: (id: string) => void
  onConfirm: () => void
  onCancel: () => void
  saving: boolean
  error: string | null
}

function DeleteRoleModal({
  role,
  roles,
  builtInRoles,
  reassignTo,
  onReassignChange,
  onConfirm,
  onCancel,
  saving,
  error,
}: DeleteRoleModalProps): React.ReactElement {
  return (
    <Modal open={true} onClose={onCancel} size="md" theme="light" variant="danger">
      <Modal.Header onClose={onCancel}>
        <span className="flex items-center gap-2">
          <i className="fa fa-exclamation-triangle" />
          Delete Role
        </span>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm mb-4">
            {error}
          </div>
        )}

        <p className="text-grey mb-4">
          Are you sure you want to delete the role <strong>"{role.name}"</strong>?
        </p>

        <div>
          <label className="block text-sm font-medium text-secondary mb-1">
            Reassign users to role (optional)
          </label>
          <select
            value={reassignTo}
            onChange={(e) => onReassignChange(e.target.value)}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">No reassignment</option>
            <optgroup label="Built-in Roles">
              {builtInRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </optgroup>
            {roles.length > 0 && (
              <optgroup label="Custom Roles">
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <p className="text-xs text-grey mt-1">
            Users assigned to this role will be moved to the selected role
          </p>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded text-grey hover:bg-gray-100"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={saving}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium disabled:opacity-50"
        >
          {saving ? (
            <>
              <i className="fa fa-spinner fa-spin mr-2" />
              Deleting...
            </>
          ) : (
            'Delete Role'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default RoleManagement
